import { AudienceIntelligence, Brand, BrandIntelligence, CompetitiveIntel, CreativeSystem, MarketIntelligence, SocialAndDigital } from "@/schema/brand-intelligence";

const brand: Brand = {
  name: "Aparna Constructions and Estates Private Limited",
  industry: ["Real Estate Development"],
  sub_industry: ["Residential Gated Communities", "Commercial Real Estate"],
  founded_year: 1996,
  founded_original_name: "Aparna Constructions and Estates Private Limited",
  headquarters: ["Hyderabad, Telangana, India"],
  tagline_primary: "Shaping dreams into reality",
  mission: '"To help build a new India by transforming lives, landscapes and living habitats of people."',
  vision: '"To deliver world-class gated communities through technology and become a brand synonymous with trust and growth."',
  overview:
    "Aparna Constructions is a Hyderabad-based real estate company specializing in premium gated communities and apartments. Since 1996, it has completed 38 developments spanning 20 million sq ft across Telangana, Andhra Pradesh, and Karnataka.",
  scale: {
    revenue: 2590,
    employees_estimate: 1500,
    markets_served_count: 3,
    operating_countries_count: 1,
  },
  global_presence: {
    regions: [
      {
        region: "Telangana",
        headquarters: "Hyderabad, Telangana, India",
        active_markets: ["Hyderabad"],
        market_type: "Owned and Direct Development",
      },
      {
        region: "Karnataka",
        headquarters: "Bengaluru, Karnataka, India",
        active_markets: ["Bengaluru", "K. Narayanapura", "Yeshwanthpur"],
        market_type: "Owned and Direct Development",
      },
      {
        region: "Andhra Pradesh",
        headquarters: "Vijayawada/Amaravati, Andhra Pradesh, India",
        active_markets: ["Vijayawada", "Amaravati", "Tadepalli"],
        market_type: "Owned and Direct Development",
      },
    ],
    key_markets_by_revenue_share: [
      {
        region: "India - Telangana (Hyderabad)",
        share_percent: 75,
        notes: 'Largest market - approximately 70-80% of operations (estimated based on "second largest real estate firm in Hyderabad" and primary operational base)',
      },
      {
        region: "India - Andhra Pradesh (Vijayawada/Amaravati)",
        share_percent: 12.5,
        notes: "Approximately 10-15% of operations (estimated based on project presence)",
      },
      {
        region: "India - Karnataka (Bengaluru)",
        share_percent: 12.5,
        notes: "Approximately 10-15% of operations (estimated based on project presence)",
      },
    ],
  },
};

const creativeSystem: CreativeSystem = {
  visual_identity: {
    colors: {
      primary: [
        {
          name: "Aparna Green",
          hex: "#2D5F3F",
        },
        {
          name: "Aparna Dark Green",
          hex: "#1A4028",
        },
        {
          name: "White",
          hex: "#FFFFFF",
        },
      ],
      secondary: [
        {
          name: "Warm Gray",
          hex: "#6B6B6B",
        },
        {
          name: "Light Gray",
          hex: "#F5F5F5",
        },
        {
          name: "Charcoal",
          hex: "#333333",
        },
      ],
      accent: [
        {
          name: "Gold/Amber",
          hex: "#C9A961",
        },
        {
          name: "Sky Blue",
          hex: "#4A90E2",
        },
      ],
    },
    typography: [
      {
        font_family: "Sans-serif",
        weight: "Bold (700), Semi-Bold (600)",
        usage: "Main headlines, hero text, project names",
      },
      {
        font_family: "Sans-serif",
        weight: "Regular (400), Medium (500)",
        usage: "Paragraph text, descriptions, general content",
      },
      {
        font_family: "Custom or modified serif/sans hybrid",
        weight: "Medium to Bold",
        usage: '"APARNA" wordmark in logo only',
      },
    ],
    logos: [
      {
        variant: "Primary",
        url: "https://d2tdzhum1kggza.cloudfront.net/website/aparna-logo.svg",
      },
      {
        variant: "Icon/Symbol Mark",
        url: "https://d2tdzhum1kggza.cloudfront.net/website/aparna-logo.svg",
      },
      {
        variant: "Reversed/White Logo",
        url: "https://d2tdzhum1kggza.cloudfront.net/website/aparna-logo.svg",
      },
      {
        variant: "Single-color version",
        url: "https://d2tdzhum1kggza.cloudfront.net/website/aparna-logo.svg",
      },
    ],
  },
  verbal_identity: {
    voice_traits: ["Trustworthy", "Aspirational", "Premium yet Accessible", "Environmentally Conscious", "Customer-Centric", "Professional", "Forward-Thinking"],
    taglines: ["Shaping dreams into reality", "Lead the future", "Building Dreams. Creating Memories. Bringing People Together.", "That's Aparna Group For You!"],
    messaging_dos: ["Emphasize transparency and quality", "Highlight ISO certifications and experience", "Focus on sustainability and eco-friendly initiatives"],
    messaging_donts: ["Avoid overly aggressive sales language", "Avoid discount-heavy messaging", "Avoid rushed/urgent pressure tactics"],
    banned_words: [],
  },
  creative_assets: [
    "https://d2tdzhum1kggza.cloudfront.net/website/aparna-logo.svg",
    "https://dyjz00pttbyut.cloudfront.net/gallery/sarovar-grande-images-for-elevation-of-aparna-sarovar-grande-645834-751848",
    "https://dyjz00pttbyut.cloudfront.net/gallery/cyber-zon-images-for-elevation-of-aparna-cyberzon-645624-751676",
  ],
  compliance: {
    brand_safety_guidelines: [],
    copyright_trademark_usage: [],
  },
  brand_architecture: {
    available_brands: [
      "Aparna Constructions and Estates Private Limited",
      "Aparna Enterprises Limited",
      "Aparna Property Management Services",
      "ANSWER (Aparna Novel Society for Welfare and Research)",
    ],
    selected_brand: "Aparna Constructions and Estates Private Limited",
  },
  ai_creative_memory: {
    design_memory: {
      pattern_accuracy: "94%",
      patterns_learned: 342,
      avg_generation: "1.5s",
    },
    style_drift_alerts: [
      {
        title: "Sarovar Grande Campaign Banner",
        description: "Accent color deviates from Aparna Gold #C9A961",
        severity: "high",
      },
      {
        title: "Project Launch Landing Page",
        description: "Heading font size inconsistent with typography scale",
        severity: "medium",
      },
      {
        title: "Social Media Ad - Instagram",
        description: "Logo spacing does not meet minimum clearance requirements",
        severity: "low",
      },
      {
        title: "Cyberscape Brochure",
        description: "Secondary gray usage exceeds recommended 20% threshold",
        severity: "medium",
      },
    ],
    preference_learning: [
      { preference: "Luxury Real Estate", score: 92, change: 5 },
      { preference: "Gold Accents", score: 88, change: 4 },
      { preference: "Trust & Prestige", score: 85, change: 3 },
      { preference: "Clean Layout", score: 81, change: 6 },
    ],
  },
};

const marketIntelligence: MarketIntelligence = {
  kpis: {
    sentiment: 6.2,
    sentiment_graph: [
      {
        metric: "Customer Sentiment Score",
        value: "6.2/10",
        delta: "-0.5",
      },
      {
        metric: "Market Position (Hyderabad)",
        value: "#2",
        delta: "Stable",
      },
      {
        metric: "Brand Awareness (Hyderabad)",
        value: "~12-15%",
        delta: "+1.2%",
      },
      {
        metric: "Employee Satisfaction (eNPS)",
        value: "58%",
        delta: "-2%",
      },
      {
        metric: "Project Delivery Track Record",
        value: "60+",
        delta: "+3 projects",
      },
      {
        metric: "Sales Velocity (FY2024)",
        value: "3.5 msf",
        delta: "-44%",
      },
      {
        metric: "Revenue Collections (FY2024)",
        value: "₹2,381 Cr",
        delta: "-30%",
      },
      {
        metric: "Construction Quality Rating",
        value: "7/10",
        delta: "-1.0",
      },
    ],
  },
  sentiment_summary: {
    time_range: ["Last 12-18 months (2024-2025)"],
    themes: {
      positive: [
        "Quality Construction Standards",
        "Brand Reputation & Trust",
        "Premium Gated Communities",
        "ISO Certifications & Standards",
        "Amenities & Location",
        "Backward Integration",
        "Customer Service (Pre-Sale)",
      ],
      neutral: ["Market Positioning", "Pricing Strategy", "Project Scale", "Expansion Efforts", "Sales Performance"],
      negative: ["Project Delays", "Poor Post-Handover Service", "Construction Quality Issues", "Customer Service Responsiveness", "Maintenance Problems", "Refund Issues", "Communication Gaps"],
    },
  },
  distribution: [
    {
      brand: "Prestige Group",
      share: 18,
    },
    {
      brand: "Aparna Constructions",
      share: 12,
    },
    {
      brand: "My Home Constructions",
      share: 10,
    },
    {
      brand: "Godrej Properties",
      share: 8,
    },
    {
      brand: "Others",
      share: 52,
    },
  ],
  sentiment_by_product: [
    {
      product: "Aparna Sarovar",
      sentiment: 7.5,
      change_delta: 0.5,
    },
    {
      product: "Aparna Zenon",
      sentiment: 6.8,
      change_delta: 0,
    },
    {
      product: "Aparna Kanopy (Series)",
      sentiment: 7,
      change_delta: 0.3,
    },
    {
      product: "Aparna Sarovar Zenith",
      sentiment: 4.5,
      change_delta: -2.5,
    },
    {
      product: "Aparna Cyberscape",
      sentiment: 5,
      change_delta: -1.8,
    },
    {
      product: "Aparna CyberLife",
      sentiment: 5.2,
      change_delta: -1.5,
    },
    {
      product: "Aparna Synergy",
      sentiment: 7.2,
      change_delta: 1,
    },
    {
      product: "Aparna Newlands",
      sentiment: 7,
      change_delta: 0,
    },
  ],
  top_topics: {
    praise: [
      {
        topic: "Quality of Construction Materials",
        weight: 10,
      },
      {
        topic: "Gated Community Design & Amenities",
        weight: 9,
      },
      {
        topic: "Brand Reputation & Trust",
        weight: 8,
      },
      {
        topic: "Location & Connectivity",
        weight: 8,
      },
      {
        topic: "ISO Certifications & Standards",
        weight: 7,
      },
      {
        topic: "Pre-Sales Customer Service",
        weight: 6,
      },
      {
        topic: "Sustainability Focus",
        weight: 5,
      },
    ],
    complaints: [
      {
        topic: "Project Delivery Delays",
        weight: 10,
      },
      {
        topic: "Poor Post-Handover Support",
        weight: 9,
      },
      {
        topic: "Construction Quality Defects",
        weight: 8,
      },
      {
        topic: "Customer Service Unresponsiveness",
        weight: 8,
      },
      {
        topic: "Maintenance Issues",
        weight: 7,
      },
      {
        topic: "Communication & Transparency Problems",
        weight: 6,
      },
      {
        topic: "Refund Processing Delays",
        weight: 5,
      },
    ],
  },
};

const competitiveIntel: CompetitiveIntel = {
  primary_competitors: [
    {
      name: "My Home Constructions Private Limited",
      threat_level: "high",
      market_share: 12,
      sentiment: 72,
    },
    {
      name: "Prestige Estates Projects Limited",
      threat_level: "high",
      market_share: 15,
      sentiment: 75,
    },
    {
      name: "Godrej Properties Limited",
      threat_level: "high",
      market_share: 8,
      sentiment: 78,
    },
    {
      name: "Brigade Enterprises Limited",
      threat_level: "medium",
      market_share: 6,
      sentiment: 70,
    },
    {
      name: "Janapriya Engineers Syndicate Pvt Ltd",
      threat_level: "medium",
      market_share: 5,
      sentiment: 68,
    },
    {
      name: "Hallmark Builders",
      threat_level: "medium",
      market_share: 4,
      sentiment: 73,
    },
    {
      name: "Rajapushpa Properties Pvt Ltd",
      threat_level: "medium",
      market_share: 4,
      sentiment: 69,
    },
    {
      name: "ASBL Developers",
      threat_level: "medium",
      market_share: 3,
      sentiment: 71,
    },
  ],
  secondary_competitors: [
    {
      name: "Lodha Group",
      threat_level: "medium",
      market_share: 3,
      sentiment: 0,
    },
    {
      name: "Sobha Limited",
      threat_level: "medium",
      market_share: 2,
      sentiment: 0,
    },
    {
      name: "Ramky Estates & Farms Limited",
      threat_level: "medium",
      market_share: 9,
      sentiment: 0,
    },
    {
      name: "SMR Holdings",
      threat_level: "medium",
      market_share: 10,
      sentiment: 0,
    },
    {
      name: "Cybercity Builders",
      threat_level: "medium",
      market_share: 16,
      sentiment: 0,
    },
    {
      name: "Praneeth Group",
      threat_level: "medium",
      market_share: 12,
      sentiment: 0,
    },
  ],
  share_of_voice: [
    {
      channel: "Digital/Social Media",
      distribution: {
        "Prestige Group": 22,
        "My Home Constructions": 18,
        "Aparna Constructions": 15,
        "Godrej Properties": 14,
        "Brigade Group": 10,
        Janapriya: 6,
        "Hallmark Builders": 5,
        Others: 10,
      },
    },
    {
      channel: "Search/SEO",
      distribution: {
        "Aparna Constructions": 18,
        "My Home Constructions": 16,
        "Prestige Group": 15,
        "Godrej Properties": 12,
        Janapriya: 8,
        "Brigade Group": 7,
        "Hallmark Builders": 4,
        Others: 20,
      },
    },
    {
      channel: "Traditional Media (Print/Outdoor)",
      distribution: {
        "My Home Constructions": 20,
        "Prestige Group": 18,
        "Aparna Constructions": 16,
        "Brigade Group": 12,
        "Godrej Properties": 10,
        Janapriya: 6,
        "Hallmark Builders": 5,
        Others: 13,
      },
    },
    {
      channel: "Events/Property Shows",
      distribution: {
        "My Home Constructions": 19,
        "Aparna Constructions": 17,
        "Prestige Group": 16,
        "Godrej Properties": 13,
        "Brigade Group": 7,
        "Hallmark Builders": 9,
        Janapriya: 5,
        Others: 14,
      },
    },
  ],
  competitive_benchmarking: [
    {
      metric_name: "Brand Awareness Score 2025",
      brands: [
        {
          brand_name: "Prestige Group",
          score: 88,
        },
        {
          brand_name: "My Home Constructions",
          score: 85,
        },
        {
          brand_name: "Aparna Constructions",
          score: 82,
        },
        {
          brand_name: "Godrej Properties",
          score: 80,
        },
        {
          brand_name: "Brigade Group",
          score: 72,
        },
        {
          brand_name: "Janapriya",
          score: 68,
        },
        {
          brand_name: "Hallmark Builders",
          score: 65,
        },
      ],
    },
    {
      metric_name: "Customer Satisfaction Index 2025",
      brands: [
        {
          brand_name: "Hallmark Builders",
          score: 85,
        },
        {
          brand_name: "Aparna Constructions",
          score: 78,
        },
        {
          brand_name: "My Home Constructions",
          score: 76,
        },
        {
          brand_name: "Prestige Group",
          score: 82,
        },
        {
          brand_name: "Godrej Properties",
          score: 80,
        },
        {
          brand_name: "ASBL Developers",
          score: 79,
        },
        {
          brand_name: "Brigade Group",
          score: 75,
        },
      ],
    },
    {
      metric_name: "Net Promoter Score 2025 (Normalized)",
      brands: [
        {
          brand_name: "Prestige Group",
          score: 72,
        },
        {
          brand_name: "Godrej Properties",
          score: 70,
        },
        {
          brand_name: "My Home Constructions",
          score: 68,
        },
        {
          brand_name: "Aparna Constructions",
          score: 67,
        },
        {
          brand_name: "Brigade Group",
          score: 65,
        },
        {
          brand_name: "Hallmark Builders",
          score: 69,
        },
      ],
    },
    {
      metric_name: "Brand Trust Quotient 2025",
      brands: [
        {
          brand_name: "Godrej Properties",
          score: 90,
        },
        {
          brand_name: "Prestige Group",
          score: 86,
        },
        {
          brand_name: "My Home Constructions",
          score: 83,
        },
        {
          brand_name: "Aparna Constructions",
          score: 81,
        },
        {
          brand_name: "Brigade Group",
          score: 78,
        },
        {
          brand_name: "Janapriya",
          score: 74,
        },
        {
          brand_name: "ASBL Developers",
          score: 76,
        },
      ],
    },
    {
      metric_name: "On-Time Delivery Score 2025",
      brands: [
        {
          brand_name: "Aparna Constructions",
          score: 84,
        },
        {
          brand_name: "My Home Constructions",
          score: 80,
        },
        {
          brand_name: "Prestige Group",
          score: 82,
        },
        {
          brand_name: "Hallmark Builders",
          score: 83,
        },
        {
          brand_name: "ASBL Developers",
          score: 81,
        },
        {
          brand_name: "Godrej Properties",
          score: 78,
        },
        {
          brand_name: "Janapriya",
          score: 76,
        },
      ],
    },
    {
      metric_name: "Construction Quality Index 2025",
      brands: [
        {
          brand_name: "Prestige Group",
          score: 88,
        },
        {
          brand_name: "Aparna Constructions",
          score: 85,
        },
        {
          brand_name: "My Home Constructions",
          score: 83,
        },
        {
          brand_name: "Sobha Limited",
          score: 90,
        },
        {
          brand_name: "Hallmark Builders",
          score: 84,
        },
        {
          brand_name: "Brigade Group",
          score: 82,
        },
        {
          brand_name: "ASBL Developers",
          score: 81,
        },
      ],
    },
  ],
  positioning: {
    competitive_advantages: [
      "Strong Local Heritage: Second-largest real estate firm in Hyderabad with 29 years of operations",
      "Quality Certifications: ISO 9001:2015, ISO 14001:2015, and OHSAS 18001:2007 certified",
      "LinkedIn Dominance: Strongest LinkedIn presence (158K followers) among local competitors",
      "Gated Community Expertise: Specialized focus on premium gated communities",
      "Backward Integration: Sister company Aparna Enterprises provides building materials",
    ],
    improvement_areas: [
      "Instagram Presence: Significantly lower followers (17K) compared to Prestige (104K) and My Home (100K)",
      "Market Share: Behind Prestige Group and needs to compete with Godrej's rapid expansion",
      "Pan-India Presence: Limited compared to Prestige and Godrej who operate nationally",
      "NPS Score: Industry-wide challenge with construction NPS at 34 in 2025",
    ],
    disruption_watchlist: [
      "Godrej Properties Expansion: Rs 2,600 crore sales in Hyderabad in 2025 showing aggressive growth",
      "Digital-First Competitors: Over 80% of Hyderabad buyers rely on search engines, YouTube, and social media",
      "New Entrants: APR Group (founded 2016) growing at ~25% YoY",
      "Premium Segment Pressure: Prestige and Brigade targeting same luxury segment",
    ],
  },
};

const audienceIntelligence: AudienceIntelligence = {
  audience_summary:
    "Working professionals seeking minimal commute; top-tier professionals from IT firms investing heavily, seeking high-end residential projects near business districts like Financial District. Age 28-42, IT/corporate professionals, household income ₹18-25 lakh annually, 75%+ college-educated. Security, amenities, and brand reputation are top priorities. Demand for gated communities has seen significant rise driven by desire for safety, privacy, and high-quality living environment; particularly popular in areas like Gachibowli, Narsingi, and Kokapet. Highly active online researchers; 96% begin search digitally; expect virtual tours, mobile-optimized experiences, and seamless WhatsApp communication. 4-9 month consideration cycle; multiple touchpoints across digital and physical channels; family-involved decision-making with emphasis on location, builder reputation, and long-term value.",
  core_demographics: {
    primary_age_range: "28-42 years",
    gender_split: {
      male: 65,
      female: 35,
    },
    income_median_usd: 26000,
    location_type: "Urban/Suburban",
    demographics_distribution: {
      age: [
        { age_range: "18-27", percentage: 12 },
        { age_range: "28-37", percentage: 38 },
        { age_range: "38-42", percentage: 22 },
        { age_range: "43-54", percentage: 20 },
        { age_range: "55+", percentage: 8 },
      ],
      education: [
        { education_level: "Bachelor's+", percentage: 58 },
        { education_level: "Graduate Degree", percentage: 24 },
        { education_level: "Some College", percentage: 12 },
        { education_level: "High School", percentage: 6 },
      ],
      geographic: [
        { location: "Urban", percentage: 52 },
        { location: "Suburban", percentage: 38 },
        { location: "Rural", percentage: 10 },
      ],
    },
  },
  occupation_distribution: [
    { occupation: "IT & Software Professionals", percentage: 35 },
    { occupation: "Corporate / Management", percentage: 22 },
    { occupation: "Entrepreneurs & Business Owners", percentage: 15 },
    { occupation: "Healthcare & Education", percentage: 12 },
    { occupation: "Government & PSU Employees", percentage: 16 },
  ],
  interests: {
    top_categories: [
      {
        category: "Real Estate & Property Investment",
        score: 9.5,
      },
      {
        category: "Technology & Smart Home Solutions",
        score: 8.8,
      },
      {
        category: "Fitness & Wellness",
        score: 8.5,
      },
      {
        category: "Travel & Leisure",
        score: 8.2,
      },
      {
        category: "Automobiles",
        score: 7.8,
      },
      {
        category: "Finance & Investment",
        score: 7.5,
      },
      {
        category: "Education",
        score: 7.3,
      },
      {
        category: "Home Décor & Interior Design",
        score: 7,
      },
      {
        category: "Dining & Entertainment",
        score: 6.8,
      },
      {
        category: "Sports & Recreation",
        score: 6.5,
      },
    ],
    brand_affinities: [
      {
        brand: "Prestige Group",
        affinity: 72,
      },
      {
        brand: "Sobha Limited",
        affinity: 68,
      },
      {
        brand: "Brigade Group",
        affinity: 65,
      },
      {
        brand: "Amazon India",
        affinity: 78,
      },
      {
        brand: "HDFC Bank",
        affinity: 75,
      },
      {
        brand: "Maruti Suzuki/Hyundai",
        affinity: 70,
      },
      {
        brand: "Asian Paints/Berger",
        affinity: 68,
      },
      {
        brand: "Godrej Interio",
        affinity: 65,
      },
      {
        brand: "Flipkart",
        affinity: 72,
      },
      {
        brand: "Swiggy/Zomato",
        affinity: 70,
      },
    ],
  },
  behavioral_insights: {
    shopping_behavior: {
      purchase_channel_distribution: {
        "Site Visits (Direct)": 42,
        "Property Portals (Online)": 28,
        "Referrals (Word-of-Mouth)": 18,
        "Real Estate Agents/Brokers": 8,
        "Digital Ads & Social Media": 4,
      },
      purchase_frequency: "0.8-1.2 times per lifetime",
      avg_order_value_usd: 120000,
    },
    decision_journey: {
      stages: [
        {
          stage: 1,
          name: "Discovery & Initial Research",
          description: "Online searches, property portals, social media ads, word-of-mouth; identifying locations and budget",
          duration_days: 45,
        },
        {
          stage: 2,
          name: "Evaluation & Comparison",
          description: "Compare amenities, price points, location and online/peer reviews; property websites provide easily digestible information; virtual tours, builder reputation checks",
          duration_days: 60,
        },
        {
          stage: 3,
          name: "Physical Inspection & Negotiation",
          description: "Visit and tour multiple properties before signing; property staff influence experience; family consultations, legal due diligence, RERA verification",
          duration_days: 37,
        },
        {
          stage: 4,
          name: "Purchase Commitment",
          description: "Token payment, home loan approval, agreement signing, documentation; financial planning and tax benefits evaluation",
          duration_days: 22,
        },
        {
          stage: 5,
          name: "Handover & Settlement",
          description: "Final payment, registration, possession, interior setup; community integration, referrals to network",
          duration_days: 135,
        },
      ],
    },
    media_and_device_usage: {
      device_preference: {
        Mobile: 58,
        "Desktop/Laptop": 35,
        Tablet: 7,
      },
      media_channels: {
        "Google Search (SEO/SEM)": 28,
        Facebook: 22,
        "Property Portals": 18,
        YouTube: 12,
        Instagram: 10,
        WhatsApp: 6,
        LinkedIn: 2,
        "Email Marketing": 2,
      },
    },
  },
  platforms: {
    distribution: {
      "Google Search": 28,
      Facebook: 22,
      "Property Portals": 18,
      YouTube: 12,
      Instagram: 10,
      WhatsApp: 6,
      LinkedIn: 2,
      Email: 2,
    },
  },
};

const socialAndDigital: SocialAndDigital = {
  platform_metrics: [
    {
      platform: "LinkedIn",
      followers: 158094,
      health_score: 85,
    },
    {
      platform: "Instagram",
      followers: 17000,
      health_score: 70,
    },
    {
      platform: "Facebook",
      followers: 0,
      health_score: 60,
    },
    {
      platform: "Twitter/X",
      followers: 0,
      health_score: 40,
    },
    {
      platform: "YouTube",
      followers: 0,
      health_score: 20,
    },
    {
      platform: "TikTok",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "Pinterest",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "Website",
      followers: 0,
      health_score: 85,
    },
  ],
  sentiment_distribution: [
    {
      label: "Positive",
      percentage: 40,
    },
    {
      label: "Neutral",
      percentage: 40,
    },
    {
      label: "Negative",
      percentage: 20,
    },
  ],
  recent_comments: [],
  engagement: {
    engagement_by_platform: [
      {
        platform: "Instagram",
        engagement_rate: 3.7,
      },
      {
        platform: "LinkedIn",
        engagement_rate: 3.5,
      },
      {
        platform: "Facebook",
        engagement_rate: 2,
      },
      {
        platform: "Twitter/X",
        engagement_rate: 50,
      },
      {
        platform: "YouTube",
        engagement_rate: 0,
      },
      {
        platform: "Website",
        engagement_rate: 10,
      },
    ],
    best_performing_content_types: [
      {
        content_type: "Instagram Reels / Short-form Video",
        performance: 9,
      },
      {
        content_type: "Instagram Carousels",
        performance: 8,
      },
      {
        content_type: "Stories / BTS Posts",
        performance: 7,
      },
      {
        content_type: "LinkedIn Articles / Long-form Posts",
        performance: 6,
      },
      {
        content_type: "Video Testimonials / User-Generated Content",
        performance: 5,
      },
      {
        content_type: "Single Image Posts",
        performance: 4,
      },
      {
        content_type: "Mixed (Images, Videos, Articles)",
        performance: 5,
      },
    ],
    follower_growth_trend: [
      {
        month: "January 2025",
        followers: 108094,
      },
      {
        month: "February 2025",
        followers: 167800,
      },
      {
        month: "March 2025",
        followers: 142900,
      },
      {
        month: "April 2025",
        followers: 208094,
      },
    ],
  },
};

export const mBrandIntelligenceAparnaConstructions: BrandIntelligence = {
  brand,
  creative_system: creativeSystem,
  market_intelligence: marketIntelligence,
  competitive_intel: competitiveIntel,
  audience_intelligence: audienceIntelligence,
  social_and_digital: socialAndDigital,
};
